import axios from "axios";
import {
  useQueries,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { useRef } from "react";
import { DLQItem, Stage } from "../lusca-search-api/generated-src";
import { getAppSetting } from "../config/AppSettings";
import { DlqInfo } from "./useFetchDlqInfo";

interface BatchDlqResult {
  status: "PENDING" | "COMPLETED" | "ERROR";
  queryExecutionId?: string;
  dlqByOrder?: Record<
    string,
    {
      inDLQ: boolean;
      dlqItems: DLQItem[];
    }
  >;
}

const MAX_BATCH_ORDER_IDS = 100;
const loadingDlqInfo: DlqInfo = { status: "loading", error: null };

function chunkOrderIds(orderIds: string[]): string[][] {
  const chunks: string[][] = [];
  for (let index = 0; index < orderIds.length; index += MAX_BATCH_ORDER_IDS) {
    chunks.push(orderIds.slice(index, index + MAX_BATCH_ORDER_IDS));
  }
  return chunks;
}

/** One Athena query per at-most-100 resolved orders in a UAT Project. */
export default function useBatchDlqInfo(
  stage: Stage,
  transactionIds: string[]
): Record<string, DlqInfo> {
  const queryClient = useQueryClient();
  const restartedQueries = useRef(new Set<string>());
  const uniqueOrderIds = Array.from(new Set(transactionIds)).sort();
  const orderIdChunks = chunkOrderIds(uniqueOrderIds);

  const batchQueries = useQueries({
    queries: orderIdChunks.map((chunk) => {
      const orderIds = chunk.join(",");
      const queryKey = ["dlqInfoBatch", stage, orderIds];
      const restartKey = `${stage}:${orderIds}`;

      return {
        queryKey,
        queryFn: async (): Promise<BatchDlqResult> => {
          const previous = queryClient.getQueryData<BatchDlqResult>(queryKey);
          const apiUrl = getAppSetting("apiUrl") as string;
          const response = await axios.get<BatchDlqResult>(
            `https://${apiUrl}/jwt/dlq-info`,
            {
              params: {
                stage,
                orderIds,
                queryExecutionId: previous?.queryExecutionId,
              },
            }
          );
          return response.data;
        },
        enabled: Boolean(stage && orderIds),
        refetchOnWindowFocus: false,
        retry: 2,
        refetchInterval: (result: BatchDlqResult | undefined) =>
          result?.status === "PENDING" ? 1000 : false,
        onSuccess: (result: BatchDlqResult) => {
          if (result.status !== "ERROR" || !result.queryExecutionId) {
            return;
          }

          queryClient.setQueryData<BatchDlqResult>(queryKey, (current) =>
            current
              ? { ...current, queryExecutionId: undefined }
              : { ...result, queryExecutionId: undefined }
          );
          if (!restartedQueries.current.has(restartKey)) {
            restartedQueries.current.add(restartKey);
            queryClient.invalidateQueries(queryKey).catch(() => undefined);
          }
        },
        onError: () => {
          queryClient.setQueryData<BatchDlqResult>(queryKey, (current) =>
            current?.queryExecutionId
              ? { ...current, queryExecutionId: undefined }
              : current
          );
        },
      };
    }),
  }) as UseQueryResult<BatchDlqResult, Error>[];

  const batchErrors = batchQueries
    .map((query) => query.error)
    .filter((error): error is Error => error instanceof Error);
  const completedByOrder = batchQueries.reduce<
    Record<string, { inDLQ: boolean; dlqItems: DLQItem[] }>
  >(
    (byOrder, query) =>
      query.data?.status === "COMPLETED"
        ? { ...byOrder, ...(query.data.dlqByOrder ?? {}) }
        : byOrder,
    {}
  );
  const hasBatchError =
    batchErrors.length > 0 ||
    batchQueries.some((query) => query.data?.status === "ERROR");

  const getDlqInfo = (transactionId: string): DlqInfo => {
    if (hasBatchError) {
      return {
        status: "error",
        error: batchErrors[0] ?? new Error("DLQ query failed"),
      };
    }
    if (Object.prototype.hasOwnProperty.call(completedByOrder, transactionId)) {
      const result = completedByOrder[transactionId];
      return {
        status: result.inDLQ ? "inDlq" : "notInDlq",
        dlqItems: result.dlqItems,
        error: null,
      };
    }
    return loadingDlqInfo;
  };

  return transactionIds.reduce<Record<string, DlqInfo>>(
    (byOrder, transactionId) => ({
      ...byOrder,
      [transactionId]: getDlqInfo(transactionId),
    }),
    {}
  );
}
